import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useRole } from "../hooks/useAuth";
import { usePlan } from "../hooks/usePlan";
import { PlanBadge, UpgradeModal } from "../components/UpgradePrompt";
import api from "../config/api";
import toast from "react-hot-toast";

// ── Types ─────────────────────────────────────────────────────────────────────
type UserRole = "admin" | "member" | "viewer";

interface TeamData {
  members: TeamMember[];
  pendingInvites: PendingInvite[];
  seatsUsed: number;
  seatsLimit: number; // -1 = unlimited
  isAtLimit: boolean;
}

interface TeamMember {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role: UserRole;
  created_at: string;
  last_login_at?: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: UserRole;
  created_at: string;
  expires_at: string;
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: "Full access · manage team, items, and settings",
  member: "Create & edit items · cannot manage team",
  viewer: "Read-only access to all company items",
};

const ROLE_COLORS: Record<UserRole, string> = {
  admin: "bg-purple-100 text-purple-700 border-purple-200",
  member: "bg-blue-100 text-blue-700 border-blue-200",
  viewer: "bg-gray-100 text-gray-600 border-gray-200",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getInitials(name?: string, email?: string): string {
  if (name)
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  return (email?.[0] || "?").toUpperCase();
}

function timeAgo(iso?: string): string {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
const TeamSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const { canUse, requiredPlanFor } = usePlan();

  const canUseSlack = canUse("reminderWebhooks");
  const [showSlackUpgrade, setShowSlackUpgrade] = useState(false);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [seatsLimit, setSeatsLimit] = useState(-1); // -1 = unlimited
  const [isAtLimit, setIsAtLimit] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>("member");
  const [inviting, setInviting] = useState(false);

  // Role change
  const [changingRole, setChangingRole] = useState<string | null>(null);

  // Remove member
  const [removing, setRemoving] = useState<string | null>(null);

  // Slack integration
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackWebhookHint, setSlackWebhookHint] = useState<string | null>(null);
  const [slackInput, setSlackInput] = useState("");
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);
  const [slackDisconnecting, setSlackDisconnecting] = useState(false);

  // ── Fetch team ──────────────────────────────────────────────────────────────
  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<TeamData>("/team");
      setMembers(res.data.members || []);
      setInvites(res.data.pendingInvites || []);
      setSeatsUsed(res.data.seatsUsed ?? 0);
      setSeatsLimit(res.data.seatsLimit ?? -1);
      setIsAtLimit(res.data.isAtLimit ?? false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // ── Fetch Slack status ──────────────────────────────────────────────────────
  useEffect(() => {
    api
      .get("/settings/slack")
      .then((res) => {
        setSlackConnected(res.data.connected);
        setSlackWebhookHint(res.data.webhook_url_hint ?? null);
      })
      .catch(() => {});
  }, []);

  // ── Slack handlers ──────────────────────────────────────────────────────────
  const handleSlackSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slackInput.trim()) return;
    setSlackSaving(true);
    try {
      await api.put("/settings/slack", { webhook_url: slackInput.trim() });
      toast.success("Slack connected!");
      setSlackConnected(true);
      setSlackWebhookHint(`${slackInput.trim().slice(0, 40)}…`);
      setSlackInput("");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to save Slack webhook");
    } finally {
      setSlackSaving(false);
    }
  };

  const handleSlackTest = async () => {
    setSlackTesting(true);
    try {
      await api.post("/settings/slack/test");
      toast.success("Test notification sent to Slack!");
    } catch (err: any) {
      toast.error(
        err.response?.data?.error || "Failed to send test notification"
      );
    } finally {
      setSlackTesting(false);
    }
  };

  const handleSlackDisconnect = async () => {
    if (
      !window.confirm(
        "Disconnect Slack? Your team will stop receiving Slack notifications."
      )
    )
      return;
    setSlackDisconnecting(true);
    try {
      await api.delete("/settings/slack");
      setSlackConnected(false);
      setSlackWebhookHint(null);
      toast.success("Slack disconnected");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to disconnect Slack");
    } finally {
      setSlackDisconnecting(false);
    }
  };

  // ── Invite ──────────────────────────────────────────────────────────────────
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post("/team/invite", {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
      await fetchTeam();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  // ── Change role ─────────────────────────────────────────────────────────────
  const handleRoleChange = async (memberId: string, newRole: UserRole) => {
    setChangingRole(memberId);
    try {
      await api.put(`/team/${memberId}/role`, { role: newRole });
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
      toast.success("Role updated");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to update role");
    } finally {
      setChangingRole(null);
    }
  };

  // ── Remove member ───────────────────────────────────────────────────────────
  const handleRemove = async (memberId: string, memberEmail: string) => {
    if (!window.confirm(`Remove ${memberEmail} from the team?`)) return;
    setRemoving(memberId);
    try {
      await api.delete(`/team/${memberId}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success("Member removed");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to remove member");
    } finally {
      setRemoving(null);
    }
  };

  // ── Cancel invite ───────────────────────────────────────────────────────────
  const handleCancelInvite = async (inviteId: string, email: string) => {
    if (!window.confirm(`Cancel invite for ${email}?`)) return;
    try {
      await api.delete(`/team/invites/${inviteId}`);
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      toast.success("Invite cancelled");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to cancel invite");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/items")}
            className="p-1.5 rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#111827]">Team</h1>
            <p className="text-xs text-[#6B7280]">
              Manage team members and permissions
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* ── Invite section (admin only) ──────────────────────────────────── */}
        {isAdmin && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-5">
            {/* ── Seat usage meter ───────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h2 className="text-sm font-semibold text-[#111827]">
                  Team seats
                </h2>
                <span className="text-xs font-medium text-[#6B7280]">
                  {seatsLimit === -1
                    ? `${seatsUsed} used · unlimited`
                    : `${seatsUsed} / ${seatsLimit} seats used`}
                </span>
              </div>

              {seatsLimit !== -1 && (
                <>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isAtLimit
                          ? "bg-[#EF4444]"
                          : seatsUsed / seatsLimit >= 0.8
                            ? "bg-[#F59E0B]"
                            : "bg-[#2563EB]"
                      }`}
                      style={{
                        width: `${Math.min(100, (seatsUsed / seatsLimit) * 100)}%`,
                      }}
                    />
                  </div>

                  {/* At-limit banner */}
                  {isAtLimit && (
                    <div className="mt-3 flex items-start gap-2.5 bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3">
                      <svg
                        className="w-4 h-4 text-[#EF4444] mt-0.5 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                      </svg>
                      <div>
                        <p className="text-xs font-semibold text-[#DC2626]">
                          Team is full ({seatsUsed}/{seatsLimit} seats)
                        </p>
                        <p className="text-xs text-[#EF4444] mt-0.5">
                          Upgrade your plan to invite more team members.{" "}
                          <a
                            href="/pricing"
                            className="font-semibold underline"
                          >
                            View plans →
                          </a>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Near-limit warning (≥ 80% but not full) */}
                  {!isAtLimit && seatsUsed / seatsLimit >= 0.8 && (
                    <p className="mt-1.5 text-xs text-[#D97706]">
                      ⚠️ Almost full — {seatsLimit - seatsUsed} seat
                      {seatsLimit - seatsUsed !== 1 ? "s" : ""} remaining.{" "}
                      <a href="/pricing" className="font-medium underline">
                        Upgrade to add more.
                      </a>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* ── Invite form (disabled when at limit) ───────────────────── */}
            <div>
              <h3 className="text-sm font-semibold text-[#111827] mb-3">
                Invite a team member
              </h3>

              {isAtLimit ? (
                <p className="text-sm text-[#6B7280] italic">
                  Remove a member or{" "}
                  <a href="/pricing" className="text-[#2563EB] font-medium hover:underline">
                    upgrade your plan
                  </a>{" "}
                  to send new invites.
                </p>
              ) : (
                <form
                  onSubmit={handleInvite}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    required
                    className="flex-1 px-3 py-2 text-sm border border-[#D1D5DB] rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as UserRole)}
                    className="px-3 py-2 text-sm border border-[#D1D5DB] rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button
                    type="submit"
                    disabled={inviting || !inviteEmail.trim()}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[#2563EB] rounded-lg
                               hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed
                               transition-colors whitespace-nowrap"
                  >
                    {inviting ? "Sending…" : "Send Invite"}
                  </button>
                </form>
              )}

              {/* Role descriptions */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {(["admin", "member", "viewer"] as UserRole[]).map((r) => (
                  <div
                    key={r}
                    className={`px-3 py-2 rounded-lg border text-xs ${ROLE_COLORS[r]}`}
                  >
                    <p className="font-semibold mb-0.5">{ROLE_LABELS[r]}</p>
                    <p className="opacity-80">{ROLE_DESCRIPTIONS[r]}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Members list ─────────────────────────────────────────────────── */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#111827]">
              Team Members
              <span className="ml-2 text-xs font-normal text-[#6B7280]">
                {members.length} {members.length === 1 ? "member" : "members"}
              </span>
            </h2>
            {seatsLimit !== -1 && (
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                  isAtLimit
                    ? "bg-red-50 text-red-600 border-red-200"
                    : seatsUsed / seatsLimit >= 0.8
                      ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                      : "bg-[#EFF6FF] text-[#2563EB] border-[#BFDBFE]"
                }`}
              >
                {seatsUsed}/{seatsLimit} seats
              </span>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" />
            </div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#6B7280]">
              No team members yet.
            </div>
          ) : (
            <ul className="divide-y divide-[#F3F4F6]">
              {members.map((m) => {
                const isSelf = m.id === user?.id;
                return (
                  <li key={m.id} className="flex items-center gap-4 px-6 py-4">
                    {/* Avatar */}
                    <div
                      className="h-9 w-9 rounded-full bg-[#EFF6FF] flex items-center justify-center
                                    text-[#2563EB] font-semibold text-sm flex-shrink-0"
                    >
                      {getInitials(m.name, m.email)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#111827] truncate">
                        {m.name || m.email}
                        {isSelf && (
                          <span className="ml-2 text-xs text-[#6B7280] font-normal">
                            (you)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-[#6B7280] truncate">
                        {m.email}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">
                        Last active: {timeAgo(m.last_login_at)}
                      </p>
                    </div>

                    {/* Role */}
                    {isAdmin && !isSelf ? (
                      <select
                        value={m.role}
                        disabled={changingRole === m.id}
                        onChange={(e) =>
                          handleRoleChange(m.id, e.target.value as UserRole)
                        }
                        className="text-xs border border-[#D1D5DB] rounded-lg px-2 py-1.5
                                   focus:outline-none focus:ring-1 focus:ring-[#2563EB] bg-white
                                   disabled:opacity-50"
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full border font-medium ${ROLE_COLORS[m.role]}`}
                      >
                        {ROLE_LABELS[m.role]}
                      </span>
                    )}

                    {/* Remove */}
                    {isAdmin && !isSelf && (
                      <button
                        onClick={() => handleRemove(m.id, m.email)}
                        disabled={removing === m.id}
                        title="Remove from team"
                        className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#EF4444]
                                   hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6h12a6 6 0 00-6-6z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17 9l4 4m0-4l-4 4"
                          />
                        </svg>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── Pending invites ──────────────────────────────────────────────── */}
        {invites.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[#111827]">
                Pending Invites
                <span className="ml-2 text-xs font-normal text-[#6B7280]">
                  {invites.length}
                </span>
              </h2>
              <button
                onClick={fetchTeam}
                disabled={loading}
                title="Refresh invites"
                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6]
                           transition-colors disabled:opacity-40"
              >
                <svg
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
            <ul className="divide-y divide-[#F3F4F6]">
              {invites.map((inv) => (
                <li key={inv.id} className="flex items-center gap-4 px-6 py-4">
                  <div
                    className="h-9 w-9 rounded-full bg-[#F3F4F6] flex items-center justify-center
                                  text-[#9CA3AF] font-semibold text-sm flex-shrink-0"
                  >
                    {inv.email[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#374151] truncate">
                      {inv.email}
                    </p>
                    <p className="text-xs text-[#9CA3AF]">
                      Expires {timeAgo(inv.expires_at)} · Invited as{" "}
                      {ROLE_LABELS[inv.role]}
                    </p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full border bg-yellow-50 text-yellow-700 border-yellow-200 font-medium">
                    Pending
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => handleCancelInvite(inv.id, inv.email)}
                      title="Cancel invite"
                      className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#EF4444]
                                 hover:bg-[#FEF2F2] transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Slack integration ────────────────────────────────────────────── */}
        {isAdmin && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-center gap-3">
              {/* Slack logo */}
              <svg
                className="w-5 h-5 flex-shrink-0"
                viewBox="0 0 124 124"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={
                  !canUseSlack ? () => setShowSlackUpgrade(true) : undefined
                }
              >
                <path
                  d="M26.3 78.4a13.2 13.2 0 0 1-13.1 13.1 13.2 13.2 0 0 1-13.1-13.1 13.2 13.2 0 0 1 13.1-13.2h13.1v13.2z"
                  fill="#E01E5A"
                />
                <path
                  d="M32.9 78.4a13.2 13.2 0 0 1 13.2-13.2 13.2 13.2 0 0 1 13.1 13.2v33a13.2 13.2 0 0 1-13.1 13.1 13.2 13.2 0 0 1-13.2-13.1v-33z"
                  fill="#E01E5A"
                />
                <path
                  d="M46.1 26.3a13.2 13.2 0 0 1-13.2-13.1A13.2 13.2 0 0 1 46.1 0a13.2 13.2 0 0 1 13.1 13.2v13.1H46.1z"
                  fill="#36C5F0"
                />
                <path
                  d="M46.1 32.9a13.2 13.2 0 0 1 13.1 13.2 13.2 13.2 0 0 1-13.1 13.1H13a13.2 13.2 0 0 1-13.1-13.1A13.2 13.2 0 0 1 13 32.9h33.1z"
                  fill="#36C5F0"
                />
                <path
                  d="M98.2 46.1a13.2 13.2 0 0 1 13.1-13.2A13.2 13.2 0 0 1 124.4 46.1a13.2 13.2 0 0 1-13.1 13.1H98.2V46.1z"
                  fill="#2EB67D"
                />
                <path
                  d="M91.5 46.1a13.2 13.2 0 0 1-13.1 13.1 13.2 13.2 0 0 1-13.2-13.1V13A13.2 13.2 0 0 1 78.4 0a13.2 13.2 0 0 1 13.1 13.1v33z"
                  fill="#2EB67D"
                />
                <path
                  d="M78.4 98.2a13.2 13.2 0 0 1 13.1 13.1 13.2 13.2 0 0 1-13.1 13.1 13.2 13.2 0 0 1-13.2-13.1V98.2h13.2z"
                  fill="#ECB22E"
                />
                <path
                  d="M78.4 91.5a13.2 13.2 0 0 1-13.2-13.1 13.2 13.2 0 0 1 13.2-13.2h33a13.2 13.2 0 0 1 13.2 13.2 13.2 13.2 0 0 1-13.2 13.1h-33z"
                  fill="#ECB22E"
                />
              </svg>
              <div>
                <h2 className="text-sm font-semibold text-[#111827] flex items-center">
                  Slack Integration
                  {!canUseSlack && (
                    <PlanBadge
                      requiredPlan={requiredPlanFor("reminderWebhooks")}
                    />
                  )}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Post expiry reminders to a Slack channel
                </p>
              </div>
              {slackConnected && canUseSlack && (
                <span
                  className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium
                                 bg-green-50 text-green-700 border border-green-200 rounded-full"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
                  Connected
                </span>
              )}
            </div>

            <div className="px-6 py-5 space-y-4">
              {!canUseSlack ? (
                <div
                  className="flex items-center gap-4 p-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg cursor-pointer"
                  onClick={() => setShowSlackUpgrade(true)}
                >
                  <span className="text-2xl">🔒</span>
                  <div>
                    <p className="text-sm font-semibold text-[#92400E]">
                      Business plan required
                    </p>
                    <p className="text-xs text-[#B45309] mt-0.5">
                      Slack notifications are available on the Business plan and
                      above.{" "}
                      <button
                        type="button"
                        className="underline font-medium"
                        onClick={() => setShowSlackUpgrade(true)}
                      >
                        Upgrade →
                      </button>
                    </p>
                  </div>
                </div>
              ) : slackConnected ? (
                <>
                  <div className="flex items-center gap-3 p-3 bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg text-sm text-[#15803D]">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>
                      Slack is connected. Your team will receive expiry
                      reminders in the configured channel.
                      {slackWebhookHint && (
                        <span className="block text-xs text-[#166534] mt-0.5 font-mono opacity-75">
                          {slackWebhookHint}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleSlackTest}
                      disabled={slackTesting}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#D1D5DB]
                                 rounded-lg text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-50 transition-colors"
                    >
                      {slackTesting ? (
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                        </svg>
                      )}
                      {slackTesting ? "Sending…" : "Send Test Notification"}
                    </button>

                    <button
                      onClick={handleSlackDisconnect}
                      disabled={slackDisconnecting}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#FECACA]
                                 rounded-lg text-[#DC2626] hover:bg-[#FEF2F2] disabled:opacity-50 transition-colors"
                    >
                      {slackDisconnecting ? "Disconnecting…" : "Disconnect"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm text-[#6B7280] space-y-1">
                    <p>To connect Slack:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>
                        Go to{" "}
                        <a
                          href="https://api.slack.com/apps"
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#2563EB] underline"
                        >
                          api.slack.com/apps
                        </a>{" "}
                        → Create New App → From scratch
                      </li>
                      <li>
                        Go to <strong>Incoming Webhooks</strong> and activate it
                      </li>
                      <li>
                        Click <strong>Add New Webhook to Workspace</strong> and
                        pick a channel
                      </li>
                      <li>Copy the webhook URL and paste it below</li>
                    </ol>
                  </div>

                  <form onSubmit={handleSlackSave} className="flex gap-2">
                    <input
                      type="url"
                      value={slackInput}
                      onChange={(e) => setSlackInput(e.target.value)}
                      placeholder="https://hooks.slack.com/services/T…/B…/…"
                      required
                      className="flex-1 px-3 py-2 text-sm border border-[#D1D5DB] rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent
                                 placeholder:text-[#9CA3AF]"
                    />
                    <button
                      type="submit"
                      disabled={slackSaving || !slackInput.trim()}
                      className="px-4 py-2 text-sm font-semibold text-white bg-[#2563EB] rounded-lg
                                 hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {slackSaving ? "Connecting…" : "Connect"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {showSlackUpgrade && (
          <UpgradeModal
            feature="Slack Integration"
            requiredPlan={requiredPlanFor("reminderWebhooks")}
            description="Send expiry reminders directly to a Slack channel with a custom Incoming Webhook."
            onClose={() => setShowSlackUpgrade(false)}
          />
        )}

        {/* ── Info for non-admins ──────────────────────────────────────────── */}
        {!isAdmin && (
          <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl px-6 py-4">
            <p className="text-sm text-[#1D4ED8]">
              <strong>ℹ️ Your role:</strong>{" "}
              {ROLE_LABELS[(user?.role as UserRole) || "member"]} —{" "}
              {ROLE_DESCRIPTIONS[(user?.role as UserRole) || "member"]}. Contact
              your team admin to change permissions.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSettings;
